/**
 * @engine/core - GameObject
 * 
 * Base class for all objects in scenes.
 * Container for components with transform hierarchy.
 */

import { Transform } from './Transform';
import type {
  GameObjectId,
  GameObjectConfig,
  GameObjectState,
  ComponentId,
  ComponentState,
  GUID,
  Tag,
  Layer,
  LevelId
} from './types';
import { generateGUID, Tags, Layers } from './types';
import { createComponentInstance, type ComponentTypeMap, type ComponentTypeId } from './ComponentTypeMap';
import type { ComponentToken } from './scripting/IEngineContext';

// ID generators
let nextGameObjectId = 1;
let nextComponentId = 1;

/**
 * Reset ID counters (for testing or scene reload)
 */
export function resetIdCounters(): void {
  nextGameObjectId = 1;
  nextComponentId = 1;
}

/**
 * GameObject - Base class for all scene objects
 * 
 * Every GameObject has:
 * - A Transform (position, rotation, scale, hierarchy)
 * - A unique ID and GUID
 * - A name, tag, and layer
 * - A list of components
 */
export class GameObject {
  // State
  private _state: GameObjectState;
  
  // Transform (always present)
  private _transform: Transform;
  
  // Components
  private _components: Map<ComponentId, ComponentState> = new Map();
  private _componentsByType: Map<string, ComponentId[]> = new Map();
  
  /** Tracks which component data objects have been hydrated with class defaults */
  private _hydratedData: WeakSet<object> = new WeakSet();

  /** @internal — set by Level to forward component lifecycle events */
  _onComponentAdded: ((go: GameObject, typeId: string, componentId: ComponentId) => void) | null = null;
  /** @internal — set by Level to forward component lifecycle events */
  _onComponentRemoved: ((go: GameObject, typeId: string, componentId: ComponentId) => void) | null = null;
  
  // Don't create directly - use Scene.createGameObject() or GameObject.create()
  constructor(config: GameObjectConfig = {}) {
    const id = nextGameObjectId++;
    
    this._state = {
      id,
      guid: config.guid ?? generateGUID(),
      name: config.name ?? 'GameObject',
      tag: config.tag ?? Tags.Untagged,
      layer: config.layer ?? Layers.Default,
      activeSelf: config.active ?? true,
      activeInHierarchy: config.active ?? true,
      isStatic: config.isStatic ?? false,
      levelId: null,
    };
    
    this._transform = new Transform(id, config.transform);
  }
  
  // ==========================================================================
  // STATIC FACTORY
  // ==========================================================================
  
  /**
   * Create a new GameObject
   */
  static create(name?: string): GameObject {
    return new GameObject(name !== undefined ? { name } : {});
  }
  
  /**
   * Create GameObject with specific config
   */
  static createWithConfig(config: GameObjectConfig): GameObject {
    return new GameObject(config);
  }
  
  // ==========================================================================
  // IDENTITY
  // ==========================================================================
  
  /** Runtime ID (unique within session) */
  get id(): GameObjectId {
    return this._state.id;
  }
  
  /** Persistent GUID (unique across saves) */
  get guid(): GUID {
    return this._state.guid;
  }
  
  /** Display name */
  get name(): string {
    return this._state.name;
  }
  
  set name(value: string) {
    this._state.name = value;
  }
  
  /** Tag for categorization */
  get tag(): Tag {
    return this._state.tag;
  }
  
  set tag(value: Tag) {
    this._state.tag = value;
  }
  
  /** Layer for rendering/physics */
  get layer(): Layer {
    return this._state.layer;
  }
  
  set layer(value: Layer) {
    this._state.layer = value;
  }
  
  // ==========================================================================
  // ACTIVE STATE
  // ==========================================================================
  
  /** Is this GameObject active (local) */
  get activeSelf(): boolean {
    return this._state.activeSelf;
  }
  
  /** Is this GameObject active in the hierarchy */
  get activeInHierarchy(): boolean {
    return this._state.activeInHierarchy;
  }
  
  /**
   * Set active state
   */
  setActive(value: boolean): void {
    if (this._state.activeSelf === value) return;
    
    this._state.activeSelf = value;
    this.updateActiveInHierarchy();
  }
  
  /** Is this a static GameObject */
  get isStatic(): boolean {
    return this._state.isStatic;
  }
  
  // ==========================================================================
  // TRANSFORM
  // ==========================================================================
  
  /** Transform component */
  get transform(): Transform {
    return this._transform;
  }
  
  // ==========================================================================
  // HIERARCHY (delegated to Transform)
  // ==========================================================================
  
  /** Parent GameObject */
  get parent(): GameObject | null {
    // TODO: Need scene reference to lookup parent by transform
    return null;
  }
  
  /**
   * Set parent
   */
  setParent(parent: GameObject | null, worldPositionStays: boolean = true): void {
    const _oldParent = this._transform.parent; void _oldParent; // TODO: use for events
    
    if (worldPositionStays && parent) {
      // Store world position before reparenting
      const worldPos = this._transform.position.clone();
      const worldRot = this._transform.rotation;
      
      this._transform.parent = parent?._transform ?? null;
      
      // Restore world position
      this._transform.position = worldPos;
      this._transform.rotation = worldRot;
    } else {
      this._transform.parent = parent?._transform ?? null;
    }
    
    this.updateActiveInHierarchy();
  }
  
  /** Number of children */
  get childCount(): number {
    return this._transform.childCount;
  }
  
  // ==========================================================================
  // COMPONENTS
  // ==========================================================================
  
  /**
   * Add a component by type ID
   * Returns the component ID
   * 
   * If a decorated component class is registered for the typeId,
   * a class instance is created with proper defaults from field
   * initializers. The `properties` object is merged over defaults.
   */
  addComponent<T>(token: ComponentToken<T>, properties?: Record<string, unknown>): ComponentId;
  addComponent(typeId: string, properties?: Record<string, unknown>): ComponentId;
  addComponent(arg: string | ComponentToken<unknown>, properties?: Record<string, unknown>): ComponentId {
    const typeId = typeof arg === 'string' ? arg : arg.typeId;
    const componentId = nextComponentId++;

    // Create a typed component instance (with defaults) if a class is registered,
    // otherwise fall back to a plain object copy.
    const data = (properties || typeId)
      ? createComponentInstance(typeId, properties)
      : undefined;
    
    const state: ComponentState = {
      id: componentId,
      typeId,
      enabled: true,
      gameObjectId: this._state.id,
      ...(data !== undefined && { data: data as Record<string, unknown> }),
    };
    
    // Mark as hydrated — addComponent already used createComponentInstance
    if (state.data) {
      this._hydratedData.add(state.data);
    }
    
    this._components.set(componentId, state);
    
    // Track by type
    if (!this._componentsByType.has(typeId)) {
      this._componentsByType.set(typeId, []);
    }
    this._componentsByType.get(typeId)!.push(componentId);

    // Notify scene events
    this._onComponentAdded?.(this, typeId, componentId);
    
    return componentId;
  }
  
  /**
   * Add a component with serialized data (for deserialization)
   */
  addComponentData(typeId: string, data: Record<string, unknown>): ComponentId {
    return this.addComponent(typeId, data);
  }
  
  /**
   * Remove a component by ID
   */
  removeComponent(componentId: ComponentId): boolean {
    const state = this._components.get(componentId);
    if (!state) return false;

    // Notify scene events before removal
    this._onComponentRemoved?.(this, state.typeId, componentId);
    
    // Remove from type map
    const typeComponents = this._componentsByType.get(state.typeId);
    if (typeComponents) {
      const index = typeComponents.indexOf(componentId);
      if (index !== -1) {
        typeComponents.splice(index, 1);
      }
      if (typeComponents.length === 0) {
        this._componentsByType.delete(state.typeId);
      }
    }
    
    this._components.delete(componentId);
    return true;
  }

  /**
   * Remove first component of type
   */
  removeComponentByType(typeId: string): boolean {
    const compId = this.getComponent(typeId);
    if (compId === null) return false;
    return this.removeComponent(compId);
  }
  
  /**
   * Get component state by ID
   */
  getComponentState(componentId: ComponentId): ComponentState | null {
    return this._components.get(componentId) ?? null;
  }

  /**
   * Get typed component data by type ID.
   * Returns the component instance with full type information, or null.
   * 
   * @example
   * ```typescript
   * const rig = go.getComponentData('core.CameraRig');
   * //    ^? CameraRig | null
   * if (rig) {
   *   rig.solo;             // boolean
   *   rig.positionDamping;  // number
   *   rig.followOffset;     // { x: number; y: number }
   * }
   * ```
   */
  getComponentData<K extends ComponentTypeId>(typeId: K): ComponentTypeMap[K] | null;
  getComponentData(typeId: string): Record<string, unknown> | null;
  getComponentData(typeId: string): unknown {
    const compId = this.getComponent(typeId);
    if (compId === null) return null;
    const state = this._components.get(compId);
    if (!state?.data) return null;

    // Lazily hydrate: if data hasn't been through createComponentInstance yet
    // (e.g., set directly via compState.data = {}), re-hydrate with class defaults.
    if (!this._hydratedData.has(state.data)) {
      const hydrated = createComponentInstance(typeId, state.data as Record<string, unknown>);
      state.data = hydrated as Record<string, unknown>;
      this._hydratedData.add(state.data);
    }

    return state.data;
  }
  
  /**
   * Token-based component access — the canonical, reflection-free path. Returns the
   * typed component data/view, or `null`. `Components` tokens come from the generated
   * `ComponentTable`, e.g. `go.getComponent(Components.SpriteRenderer)`.
   */
  getComponent<T>(token: ComponentToken<T>): T | null;
  /**
   * Legacy string-keyed access — returns the first component *instance id* of `typeId`,
   * or `null`. (Token form returns data; string form returns an id.)
   */
  getComponent(typeId: string): ComponentId | null;
  getComponent(arg: string | ComponentToken<unknown>): unknown {
    if (typeof arg !== 'string') {
      return this.getComponentData(arg.typeId);
    }
    const components = this._componentsByType.get(arg);
    return components?.[0] ?? null;
  }
  
  /**
   * Get all components of type
   */
  getComponents(typeId: string): ComponentId[] {
    return this._componentsByType.get(typeId) ?? [];
  }
  
  /**
   * Check if this object has a component of the given type (token or string typeId).
   */
  hasComponent(token: ComponentToken<unknown>): boolean;
  hasComponent(typeId: string): boolean;
  hasComponent(arg: string | ComponentToken<unknown>): boolean {
    const typeId = typeof arg === 'string' ? arg : arg.typeId;
    return this._componentsByType.has(typeId);
  }
  
  /**
   * Get all component IDs
   */
  getAllComponentIds(): ComponentId[] {
    return Array.from(this._components.keys());
  }
  
  /**
   * Get all component states
   */
  getAllComponents(): ComponentState[] {
    return Array.from(this._components.values());
  }

  /**
   * Get all component type IDs
   */
  getComponentTypes(): string[] {
    return Array.from(this._componentsByType.keys());
  }
  
  // ==========================================================================
  // SCENE
  // ==========================================================================
  
  /** Scene this object belongs to */
  get sceneId(): LevelId | null {
    return this._state.levelId;
  }
  
  /**
   * Set scene (internal - called by Scene)
   * @internal
   */
  _setScene(sceneId: LevelId | null): void {
    (this._state as { levelId: LevelId | null }).levelId = sceneId;
  }
  
  // ==========================================================================
  // PREFAB
  // ==========================================================================
  
  /** Prefab GUID if instantiated from prefab */
  get prefabGuid(): GUID | undefined {
    return this._state.prefabGuid;
  }
  
  /**
   * Set prefab reference (internal)
   * @internal
   */
  _setPrefabGuid(guid: GUID): void {
    (this._state as { prefabGuid?: GUID }).prefabGuid = guid;
  }
  
  // ==========================================================================
  // SERIALIZATION
  // ==========================================================================
  
  /**
   * Get full state for serialization
   */
  getState(): GameObjectState {
    return { ...this._state };
  }
  
  /**
   * Restore state from serialization
   * @internal
   */
  _restoreState(state: GameObjectState): void {
    this._state = { ...state };
  }
  
  /**
   * Set GUID (for deserialization)
   * @internal
   */
  _setGuid(guid: GUID): void {
    (this._state as { guid: GUID }).guid = guid;
  }
  
  // ==========================================================================
  // INTERNAL
  // ==========================================================================
  
  private updateActiveInHierarchy(): void {
    const parentActive = this._transform.parent 
      ? true // TODO: get parent GameObject's activeInHierarchy
      : true;
    
    const wasActive = this._state.activeInHierarchy;
    this._state.activeInHierarchy = this._state.activeSelf && parentActive;
    
    if (wasActive !== this._state.activeInHierarchy) {
      // Propagate to children  
      for (let i = 0; i < this._transform.childCount; i++) {
        // TODO: Get child GameObject and update
      }
    }
  }
}
